/**
 * Booking API Service
 *
 * Handles all booking-related API calls to the Go backend.
 * Ported from Vue frontend with adjustments for React/hooks architecture.
 */

import { formatAPIDate } from "@/utils/date";
import { parseISO } from "date-fns";
import { apiClient } from "./apiClient";
import type {
  IBooking,
  IBookingResponse,
  IBookingYear,
  IPostBooking,
  IRawBooking,
} from "./types";

/**
 * Allowed filter keys for booking queries
 */
const ALLOWED_BOOKING_FILTERS = [
  "employee",
  "from_date",
  "to_date",
  "project",
  "limit",
  "b_type",
  "customer",
] as const;

/**
 * Allowed filter keys for year view queries
 */
const ALLOWED_YEAR_FILTERS = ["employee", "project"] as const;

/**
 * Parse raw booking from API response to typed booking with Date objects
 */
function parseBooking(raw: IRawBooking): IBooking {
  return {
    ...raw,
    date: parseISO(raw.date),
    created_at: parseISO(raw.created_at),
    updated_at: parseISO(raw.updated_at),
  };
}

/**
 * Placeholder employee for null values
 */
const PLACEHOLDER_EMPLOYEE = {
  id: 0,
  email: "",
  department: "",
  given_name: "Unknown",
  surname: "Employee",
  name: "Unknown Employee",
  is_archived: false,
  settings: { is_bookable: false },
};

/**
 * Placeholder activity for null values
 */
const PLACEHOLDER_ACTIVITY = {
  id: 0,
  code: "",
  name: "Unknown Activity",
};

export class BookingAPI {
  private baseUrl = "/bookings";

  /**
   * Build query string from filter Map
   */
  private buildQueryString(filters: Map<string, string>): string {
    const params: string[] = [];

    filters.forEach((value, key) => {
      if (
        ALLOWED_BOOKING_FILTERS.includes(
          key as (typeof ALLOWED_BOOKING_FILTERS)[number],
        )
      ) {
        params.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    });

    return params.length > 0 ? `?${params.join("&")}` : "";
  }

  /**
   * Normalize entity values from API response.
   *
   * The Go backend sends zero-value structs (e.g., {id: 0, email: "", ...})
   * when a field is null in the database, rather than actual null values.
   * This function detects these zero-value entities and normalizes them to null
   * so the frontend can properly handle optional relationships.
   *
   * This is critical for:
   * - Soft bookings: may have customer but no employee
   * - Absences: may have neither project nor customer
   */
  private replaceNulls(booking: IBooking): IBooking {
    // Detect zero-value entities (id === 0 means not set in DB)
    const hasValidEmployee = booking.employee && booking.employee.id > 0;
    const hasValidProject = booking.project && booking.project.id > 0;
    const hasValidCustomer = booking.customer && booking.customer.id > 0;
    const hasValidActivity = booking.activity && booking.activity.id > 0;
    const hasValidBookedBy = booking.booked_by && booking.booked_by.id > 0;

    return {
      ...booking,
      // Set to null if zero-value, otherwise keep the real entity
      employee: hasValidEmployee ? booking.employee : null,
      project: hasValidProject ? booking.project : null,
      customer: hasValidCustomer ? booking.customer : null,
      activity: hasValidActivity ? booking.activity : PLACEHOLDER_ACTIVITY,
      booked_by: hasValidBookedBy ? booking.booked_by : PLACEHOLDER_EMPLOYEE,
    };
  }

  /**
   * Fetch bookings with optional filters
   * Handles pagination automatically
   */
  async getBookings(filters?: Map<string, string>): Promise<IBooking[]> {
    const queryString = filters ? this.buildQueryString(filters) : "";
    const url = `${this.baseUrl}${queryString}`;

    const allBookings: IBooking[] = [];
    let nextUrl: string | null = url;

    // Handle pagination
    while (nextUrl) {
      const response: { data: IBookingResponse; status: number } =
        await apiClient.get<IBookingResponse>(nextUrl);
      const data: IBookingResponse = response.data;

      // Parse and add bookings
      const parsed = data.results.map((raw: IRawBooking) =>
        this.replaceNulls(parseBooking(raw)),
      );
      allBookings.push(...parsed);

      // Check for next page
      nextUrl = data.next;
    }

    return allBookings;
  }

  /**
   * Fetch aggregated booking data for year view
   */
  async getBookingsYear(
    from: Date,
    to: Date,
    filters?: Map<string, string>,
  ): Promise<IBookingYear[]> {
    const params = new URLSearchParams();
    params.set("from_date", formatAPIDate(from));
    params.set("to_date", formatAPIDate(to));

    // Add allowed filters
    if (filters) {
      filters.forEach((value, key) => {
        if (
          ALLOWED_YEAR_FILTERS.includes(
            key as (typeof ALLOWED_YEAR_FILTERS)[number],
          )
        ) {
          params.set(key, value);
        }
      });
    }

    const response = await apiClient.get<IBookingYear[]>(
      `${this.baseUrl}/year?${params.toString()}`,
    );

    return response.data;
  }

  /**
   * Create multiple bookings
   */
  async createBookings(bookings: IPostBooking[]): Promise<IBooking[]> {
    // Format dates for API
    const payload = bookings.map((b) => ({
      ...b,
      date: b.date instanceof Date ? formatAPIDate(b.date) : b.date,
    }));

    const response = await apiClient.post<IRawBooking[]>(this.baseUrl, payload);

    if (response.status === 403) {
      throw new Error("FORBIDDEN");
    }

    if (response.status >= 400) {
      throw new Error("UNKNOWN_ERROR");
    }

    return response.data.map((raw) => this.replaceNulls(parseBooking(raw)));
  }

  /**
   * Update a single booking
   */
  async updateBooking(booking: IPostBooking): Promise<IBooking> {
    if (!booking.id) {
      throw new Error("Booking ID is required for update");
    }

    // Format date for API
    const payload = {
      ...booking,
      date:
        booking.date instanceof Date
          ? formatAPIDate(booking.date)
          : booking.date,
    };

    const response = await apiClient.put<IRawBooking>(
      `${this.baseUrl}/${booking.id}`,
      payload,
    );

    if (response.status === 403) {
      throw new Error("FORBIDDEN");
    }

    if (response.status >= 400) {
      throw new Error("UNKNOWN_ERROR");
    }

    return this.replaceNulls(parseBooking(response.data));
  }

  /**
   * Update multiple bookings in a single request
   */
  async updateBookings(bookings: IPostBooking[]): Promise<IBooking[]> {
    if (bookings.length === 0) {
      return [];
    }

    const payload = bookings.map((booking) => ({
      id: booking.id,
      activity: booking.activity,
      b_type: booking.b_type,
      customer: booking.customer,
      date:
        booking.date instanceof Date
          ? formatAPIDate(booking.date)
          : booking.date,
      employee: booking.employee,
      hours: booking.hours,
      project: booking.project,
    }));

    const response = await apiClient.put<IRawBooking[]>(this.baseUrl, payload);

    if (response.status === 403) {
      throw new Error("FORBIDDEN");
    }

    if (response.status >= 400) {
      throw new Error("UNKNOWN_ERROR");
    }

    return response.data.map((raw) => this.replaceNulls(parseBooking(raw)));
  }

  /**
   * Delete a booking
   */
  async deleteBooking(booking: IBooking): Promise<void> {
    const response = await apiClient.delete(`${this.baseUrl}/${booking.id}`);

    if (response.status === 403) {
      throw new Error("FORBIDDEN");
    }

    if (response.status !== 200 && response.status !== 204) {
      throw new Error("UNKNOWN_ERROR");
    }
  }

  /**
   * Delete multiple bookings in a single request
   */
  async deleteBookings(bookings: IBooking[]): Promise<void> {
    if (bookings.length === 0) {
      return;
    }

    const response = await apiClient.delete(this.baseUrl, {
      ids: bookings.map((booking) => booking.id),
    });

    if (response.status === 403) {
      throw new Error("FORBIDDEN");
    }

    if (response.status !== 200 && response.status !== 204) {
      throw new Error("UNKNOWN_ERROR");
    }
  }

  /**
   * Check for booking conflicts before creating
   */
  async checkConflicts(bookings: IPostBooking[]): Promise<IBooking[]> {
    // Format dates for API
    const payload = bookings.map((b) => ({
      ...b,
      date: b.date instanceof Date ? formatAPIDate(b.date) : b.date,
    }));

    const response = await apiClient.post<IRawBooking[]>(
      `${this.baseUrl}/checks/conflicts`,
      payload,
    );

    return response.data.map((raw) => this.replaceNulls(parseBooking(raw)));
  }

  /**
   * Convert IBooking objects to IPostBooking format for API calls
   */
  static bookingsToPostBookings(bookings: IBooking[]): IPostBooking[] {
    return bookings.map((b) => ({
      id: b.id,
      date: b.date,
      project: b.project?.id ?? null,
      employee: b.employee?.id ?? null,
      activity: 1, // Hardcoded as in Vue version
      b_type: b.b_type,
      hours: b.hours,
      customer: b.customer?.id ?? null,
    }));
  }
}

// Export singleton instance
export const bookingAPI = new BookingAPI();
